import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import styles from './Markdown.module.css';
interface SimpleMarkdownProps {
  children: string | null | undefined;
  className?: string;
}
type SafeLinkProps = {
  href: string;
  target?: '_blank';
  rel?: 'noopener noreferrer';
};
function sameSiteKey(url: URL): string {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const portPart = url.port ? `:${url.port}` : '';
  return `${url.protocol}//${host}${portPart}`;
}
function configuredSiteKey(): string | null {
  const base = process.env.NEXT_PUBLIC_MIDDLEWARE_URL;
  if (!base) return null;
  try {
    return sameSiteKey(new URL(base));
  } catch {
    return null;
  }
}
function isSameSiteAbsoluteHttpUrl(url: URL): boolean {
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  const site = configuredSiteKey();
  if (!site) return false;
  return sameSiteKey(url) === site;
}

/** Only http(s), same-origin paths, and mailto — blocks javascript:, data:, etc. */
function getSafeLinkProps(raw: string): SafeLinkProps | null {
  const href = raw.trim();
  if (!href) return null;
  if (/^\s*(javascript:|data:|vbscript:)/i.test(href)) {
    return null;
  }
  if (href.startsWith('/') && !href.startsWith('//')) {
    return {
      href
    };
  }
  if (/^mailto:/i.test(href)) {
    return {
      href
    };
  }
  try {
    const url = new URL(href);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      if (isSameSiteAbsoluteHttpUrl(url)) {
        return {
          href
        };
      }
      return {
        href,
        target: '_blank',
        rel: 'noopener noreferrer'
      };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Parses basic inline markdown: **bold**, *italic*, [text](url).
 * Designed for short CMS content (subtitles, descriptions) where
 * a full markdown parser (102KB) is unnecessary.
 */
function parseInline(text: string): ReactNode[] {
  const result: ReactNode[] = [];
  // Negated classes prevent crossing delimiters but don't prevent quadratic backtracking:
  // n openers with no closer each trigger O(n) retries, giving O(n²) total.
  // Explicit upper bounds cap each position to O(limit), keeping overall cost O(n).
  const regex = /\*\*([^*\n]{1,1000})\*\*|\*([^*\n]{1,1000})\*|\[([^\]]{1,1000})\]\(([^)]{1,2000})\)/g;
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(regex)) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      result.push(<strong key={key++}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      result.push(<em key={key++}>{match[2]}</em>);
    } else if (match[3] !== undefined) {
      const link = getSafeLinkProps(match[4] ?? '');
      result.push(link ? <a key={key++} href={link.href} target={link.target} rel={link.rel}>
            {match[3]}
          </a> : <span key={key++}>{match[3]}</span>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result;
}
function parseBlock(block: string, key: number): ReactNode {
  const trimmed = block.trim();
  if (!trimmed) return null;
  const lines = trimmed.split('\n');
  const content: ReactNode[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) content.push(<br key={`br-${i}`} />);
    content.push(...parseInline(lines[i]));
  }
  return <p key={key}>{content}</p>;
}
const MAX_INPUT_LENGTH = 10_000;
const SimpleMarkdown = t0 => {
  const $ = _c(7);
  const {
    children,
    className
  } = t0;
  if (!children) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] !== children || $[1] !== className) {
    const input = children.length > MAX_INPUT_LENGTH ? children.slice(0, MAX_INPUT_LENGTH) : children;
    const blocks = input.split(/\n\n+/);
    t1 = `${styles.markdownContent}${className ? ` ${className}` : ""}`;
    t2 = blocks.map(_temp);
    $[0] = children;
    $[1] = className;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export { SimpleMarkdown };
function _temp(block, i) {
  return parseBlock(block, i);
}